import {
    BaseScoreResult,
    QuestionnaireResponse,
    Item,
    getAnswerCode,
    getAnswerNumber,
    findItemByLinkId
} from './types';

export interface BerlinScoreCategory {
    categoryId: string;
    totalPoints: number;
    isPositive: boolean;
    details: Record<string, number>;
}

export interface BerlinScoreResult extends BaseScoreResult {
    categories: BerlinScoreCategory[];
    positiveCategoriesCount: number;
    highRiskApnea: boolean;
    bmi?: number;
}

export function processBerlinQuestionnaire(response: QuestionnaireResponse): BerlinScoreResult {
    const rootItems = response.item || [];

    const cat1 = findItemByLinkId(rootItems, "categoria-01");
    const cat2 = findItemByLinkId(rootItems, "categoria-02");
    const cat3 = findItemByLinkId(rootItems, "categoria-03");

    // Categoria 1 mapping
    const c1_ronco = getAnswerCode(findItemByLinkId(cat1?.item, "ronco"));
    const c1_intensidade = getAnswerCode(findItemByLinkId(cat1?.item, "ronco-intensidade"));
    const c1_freq = getAnswerCode(findItemByLinkId(cat1?.item, "ronco-frequencia"));
    const c1_incomoda = getAnswerCode(findItemByLinkId(cat1?.item, "ronco-incomoda"));
    const c1_apneia = getAnswerCode(findItemByLinkId(cat1?.item, "apneia-observada"));

    const c1_details: Record<string, number> = {};
    if (c1_ronco === "1") c1_details["ronco"] = 1; else c1_details["ronco"] = 0;
    c1_details["ronco-intensidade"] = ["3", "4"].includes(c1_intensidade || "") ? 1 : 0;
    c1_details["ronco-frequencia"] = ["1", "2"].includes(c1_freq || "") ? 1 : 0;
    c1_details["ronco-incomoda"] = c1_incomoda === "1" ? 1 : 0;
    c1_details["apneia-observada"] = ["1", "2"].includes(c1_apneia || "") ? 1 : 0;

    const c1_total = Object.values(c1_details).reduce((a, b) => a + b, 0);
    const c1_positive = c1_total >= 2;

    // Categoria 2 mapping
    const c2_cansacoAcordar = getAnswerCode(findItemByLinkId(cat2?.item, "cansaco-pos-acordar"));
    const c2_cansacoDia = getAnswerCode(findItemByLinkId(cat2?.item, "cansaco-dia"));
    const c2_dirigiu = getAnswerCode(findItemByLinkId(cat2?.item, "cochilou-dirigindo"));
    const c2_dirigiuFreq = getAnswerCode(findItemByLinkId(cat2?.item, "cochilou-dirigindo-frequencia"));

    const c2_details: Record<string, number> = {};
    c2_details["cansaco-pos-acordar"] = ["1", "2"].includes(c2_cansacoAcordar || "") ? 1 : 0;
    c2_details["cansaco-dia"] = ["1", "2"].includes(c2_cansacoDia || "") ? 1 : 0;
    if (c2_dirigiu === "1") {
        c2_details["cochilou-dirigindo-frequencia"] = ["1", "2"].includes(c2_dirigiuFreq || "") ? 1 : 0;
    } else {
        c2_details["cochilou-dirigindo-frequencia"] = 0;
    }

    const c2_total = Object.values(c2_details).reduce((a, b) => a + b, 0);
    const c2_positive = c2_total >= 2;

    const c3_hta = getAnswerCode(findItemByLinkId(cat3?.item, "pressao-alta"));
    const c3_imc = getAnswerNumber(findItemByLinkId(cat3?.item, "imc"));

    const c3_details: Record<string, number> = {};
    c3_details["pressao-alta"] = c3_hta === "1" ? 1 : 0;
    c3_details["imc"] = 0;

    const c3_total = Object.values(c3_details).reduce((a, b) => a + b, 0);
    const c3_positive = c3_total >= 1 || (typeof c3_imc === "number" && c3_imc > 30);

    const categories: BerlinScoreCategory[] = [
        { categoryId: "categoria-01", totalPoints: c1_total, isPositive: c1_positive, details: c1_details },
        { categoryId: "categoria-02", totalPoints: c2_total, isPositive: c2_positive, details: c2_details },
        { categoryId: "categoria-03", totalPoints: c3_total, isPositive: c3_positive, details: c3_details },
    ];

    const positiveCategoriesCount = categories.filter((c) => c.isPositive).length;

    const louderThanTalking = ["3", "4"].includes(c1_intensidade || "");
    const snoreAtLeast3_4Week = ["1", "2"].includes(c1_freq || "");
    const complaints = c1_incomoda === "1";
    const witnessedPausesAtLeast3_4Week = ["1", "2"].includes(c1_apneia || "");
    const snoreCriteriaCount = [louderThanTalking, snoreAtLeast3_4Week, complaints, witnessedPausesAtLeast3_4Week]
        .filter(Boolean).length;
    const criterion1 = (c1_ronco === "1") && (snoreCriteriaCount >= 2);

    const fatigueMorning = ["1", "2"].includes(c2_cansacoAcordar || "");
    const fatigueDay = ["1", "2"].includes(c2_cansacoDia || "");
    const fellAsleepDriving = c2_dirigiu === "1" && ["1", "2"].includes(c2_dirigiuFreq || "");
    const criterion2 = (fatigueMorning && fatigueDay) || fellAsleepDriving;

    const criterion3 = (c3_hta === "1") || (typeof c3_imc === "number" && c3_imc > 30);

    const highRiskApnea = [criterion1, criterion2, criterion3].filter(Boolean).length >= 2;

    return {
        questionnaireId: response.questionnaire?.split("/")?.[1],
        questionnaireResponseId: response.id,
        categories,
        positiveCategoriesCount,
        highRiskApnea,
        bmi: c3_imc,
    };
}


